import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { referralService } from '../services/referralService';
import { vendorService } from '../services/vendorService';
import { telecallerService } from '../services/telecallerService';
import {
  RiderReferral,
  ReferralStatus,
  SourcingChannel,
  CreateReferralDTO,
} from '../types/referral';

const ReferralTracking: React.FC = () => {
  const [referrals, setReferrals] = useState<RiderReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTimelineDialog, setOpenTimelineDialog] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<RiderReferral | null>(null);
  const [vendors, setVendors] = useState<any[]>([]);
  const [telecallers, setTelecallers] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [telecallerFilter, setTelecallerFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page] = useState(1);
  const [limit] = useState(50);

  // Form data
  const [formData, setFormData] = useState<CreateReferralDTO>({
    candidateName: '',
    candidatePhone: '',
    candidateEmail: '',
    city: '',
    sourcingChannel: SourcingChannel.VENDOR_REFERRAL,
    vendorId: '',
    telecallerId: '',
    notes: '',
  });

  const loadReferrals = useCallback(async () => {
    try {
      setLoading(true);

      const response = await referralService.getReferrals({
        page,
        limit,
        status: statusFilter || undefined,
        sourcingChannel: channelFilter || undefined,
        vendorId: vendorFilter || undefined,
        telecallerId: telecallerFilter || undefined,
        city: cityFilter || undefined,
        search: searchTerm || undefined,
      });

      setReferrals(response.data || []);
    } catch (error) {
      console.error('❌ Error loading referrals:', error);
      setSnackbar({
        open: true,
        message: `Failed to load referrals: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, channelFilter, vendorFilter, telecallerFilter, cityFilter, searchTerm]);

  const loadVendors = useCallback(async () => {
    try {
      const response = await vendorService.getVendors({ limit: 100 });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, []);

  const loadTelecallers = useCallback(async () => {
    try {
      const response = await telecallerService.getTelecallers({ limit: 100 });
      setTelecallers(response.data || []);
    } catch (error) {
      console.error('Error loading telecallers:', error);
    }
  }, []);

  useEffect(() => {
    loadReferrals();
    loadVendors();
    loadTelecallers();
  }, [loadReferrals, loadVendors, loadTelecallers]);

  const handleOpenDialog = () => {
    setFormData({
      candidateName: '',
      candidatePhone: '',
      candidateEmail: '',
      city: '',
      sourcingChannel: SourcingChannel.VENDOR_REFERRAL,
      vendorId: '',
      telecallerId: '',
      notes: '',
    });
    setOpenDialog(true);
  };

  const handleSaveReferral = async () => {
    try {
      setLoading(true);
      await referralService.createReferral(formData);
      setSnackbar({
        open: true,
        message: 'Referral created successfully',
        severity: 'success',
      });
      setOpenDialog(false);
      loadReferrals();
    } catch (error) {
      console.error('❌ Error creating referral:', error);
      setSnackbar({
        open: true,
        message: `Failed to create referral: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewTimeline = (referral: RiderReferral) => {
    setSelectedReferral(referral);
    setOpenTimelineDialog(true);
  };

  const getStatusColor = (status: ReferralStatus): any => {
    switch (status) {
      case ReferralStatus.LEAD_CREATED:
        return 'default';
      case ReferralStatus.CONTACTED:
        return 'info';
      case ReferralStatus.REGISTERED:
        return 'primary';
      case ReferralStatus.KYC_VERIFIED:
        return 'secondary';
      case ReferralStatus.FIRST_DELIVERY_COMPLETED:
        return 'success';
      case ReferralStatus.ACTIVE:
        return 'success';
      case ReferralStatus.RETAINED_30:
      case ReferralStatus.RETAINED_90:
        return 'success';
      case ReferralStatus.CHURNED:
        return 'warning';
      case ReferralStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const groupedByStatus = referrals.reduce((acc, referral) => {
    const status = referral.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(referral);
    return acc;
  }, {} as Record<string, RiderReferral[]>);

  const statusOrder = [
    ReferralStatus.LEAD_CREATED,
    ReferralStatus.CONTACTED,
    ReferralStatus.REGISTERED,
    ReferralStatus.KYC_VERIFIED,
    ReferralStatus.FIRST_DELIVERY_COMPLETED,
    ReferralStatus.ACTIVE,
    ReferralStatus.RETAINED_30,
    ReferralStatus.RETAINED_90,
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Referral Tracking
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadReferrals}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Add Referral
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by name, phone, code"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={ReferralStatus.LEAD_CREATED}>Lead Created</MenuItem>
                  <MenuItem value={ReferralStatus.CONTACTED}>Contacted</MenuItem>
                  <MenuItem value={ReferralStatus.REGISTERED}>Registered</MenuItem>
                  <MenuItem value={ReferralStatus.KYC_VERIFIED}>KYC Verified</MenuItem>
                  <MenuItem value={ReferralStatus.FIRST_DELIVERY_COMPLETED}>First Delivery</MenuItem>
                  <MenuItem value={ReferralStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={ReferralStatus.RETAINED_30}>Retained 30 Days</MenuItem>
                  <MenuItem value={ReferralStatus.RETAINED_90}>Retained 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={channelFilter}
                  label="Channel"
                  onChange={(e) => setChannelFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={SourcingChannel.VENDOR_REFERRAL}>Vendor Referral</MenuItem>
                  <MenuItem value={SourcingChannel.TELECALLER}>Telecaller</MenuItem>
                  <MenuItem value={SourcingChannel.SOCIAL_MEDIA}>Social Media</MenuItem>
                  <MenuItem value={SourcingChannel.WEBSITE}>Website</MenuItem>
                  <MenuItem value={SourcingChannel.WALK_IN}>Walk-in</MenuItem>
                  <MenuItem value={SourcingChannel.OTHER}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={vendorFilter}
                  label="Vendor"
                  onChange={(e) => setVendorFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="City"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="Filter by city"
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setChannelFilter('');
                  setVendorFilter('');
                  setTelecallerFilter('');
                  setCityFilter('');
                }}
                sx={{ height: '56px' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pipeline View */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {statusOrder.map((status) => (
            <Grid item xs={12} md={3} key={status}>
              <Paper sx={{ p: 2, minHeight: 400 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" fontSize={14} fontWeight="bold">
                    {status.replace(/_/g, ' ')}
                  </Typography>
                  <Chip
                    label={groupedByStatus[status]?.length || 0}
                    color={getStatusColor(status)}
                    size="small"
                  />
                </Box>
                <Stack spacing={2}>
                  {groupedByStatus[status]?.map((referral) => (
                    <Card
                      key={referral.id}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 2 },
                      }}
                      onClick={() => handleViewTimeline(referral)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          {referral.candidateName}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
                          {referral.candidatePhone}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary" gutterBottom>
                          {referral.referralCode}
                        </Typography>
                        {referral.vendor && (
                          <Chip
                            label={referral.vendor.name}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1, fontSize: 10 }}
                          />
                        )}
                        {referral.telecaller && (
                          <Chip
                            label={referral.telecaller.name}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1, fontSize: 10 }}
                          />
                        )}
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                          {new Date(referral.leadCreatedAt).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Referral</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Candidate Name"
                value={formData.candidateName}
                onChange={(e) => setFormData({ ...formData, candidateName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Phone"
                value={formData.candidatePhone}
                onChange={(e) => setFormData({ ...formData, candidatePhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.candidateEmail}
                onChange={(e) => setFormData({ ...formData, candidateEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Sourcing Channel</InputLabel>
                <Select
                  value={formData.sourcingChannel}
                  label="Sourcing Channel"
                  onChange={(e) =>
                    setFormData({ ...formData, sourcingChannel: e.target.value as SourcingChannel })
                  }
                >
                  <MenuItem value={SourcingChannel.VENDOR_REFERRAL}>Vendor Referral</MenuItem>
                  <MenuItem value={SourcingChannel.TELECALLER}>Telecaller</MenuItem>
                  <MenuItem value={SourcingChannel.SOCIAL_MEDIA}>Social Media</MenuItem>
                  <MenuItem value={SourcingChannel.WEBSITE}>Website</MenuItem>
                  <MenuItem value={SourcingChannel.WALK_IN}>Walk-in</MenuItem>
                  <MenuItem value={SourcingChannel.OTHER}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.sourcingChannel === SourcingChannel.VENDOR_REFERRAL && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Vendor</InputLabel>
                  <Select
                    value={formData.vendorId}
                    label="Vendor"
                    onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                  >
                    {vendors.map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {formData.sourcingChannel === SourcingChannel.TELECALLER && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Telecaller</InputLabel>
                  <Select
                    value={formData.telecallerId}
                    label="Telecaller"
                    onChange={(e) => setFormData({ ...formData, telecallerId: e.target.value })}
                  >
                    {telecallers.map((tc) => (
                      <MenuItem key={tc.id} value={tc.id}>
                        {tc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveReferral} variant="contained" disabled={loading}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog
        open={openTimelineDialog}
        onClose={() => setOpenTimelineDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Referral Timeline - {selectedReferral?.candidateName}</DialogTitle>
        <DialogContent>
          {selectedReferral && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Referral Code
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedReferral.referralCode}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{selectedReferral.candidatePhone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    City
                  </Typography>
                  <Typography variant="body1">{selectedReferral.city}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Channel
                  </Typography>
                  <Typography variant="body1">{selectedReferral.sourcingChannel}</Typography>
                </Grid>
                {selectedReferral.vendor && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Vendor
                    </Typography>
                    <Typography variant="body1">
                      {selectedReferral.vendor.name} ({selectedReferral.vendor.phone})
                    </Typography>
                  </Grid>
                )}
                {selectedReferral.telecaller && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Telecaller
                    </Typography>
                    <Typography variant="body1">
                      {selectedReferral.telecaller.name} ({selectedReferral.telecaller.phone})
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Progression Timeline
              </Typography>
              <Timeline position="alternate">
                {selectedReferral.leadCreatedAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.leadCreatedAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="primary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Lead Created</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.contactedAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.contactedAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="info" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Contacted</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.registeredAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.registeredAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="primary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Registered</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.kycVerifiedAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.kycVerifiedAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="secondary" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">KYC Verified</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.firstDeliveryAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.firstDeliveryAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">First Delivery Completed</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.activeAt && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.activeAt).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Active</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.retained30At && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.retained30At).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Retained 30 Days</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
                {selectedReferral.retained90At && (
                  <TimelineItem>
                    <TimelineOppositeContent color="textSecondary">
                      {new Date(selectedReferral.retained90At).toLocaleString()}
                    </TimelineOppositeContent>
                    <TimelineSeparator>
                      <TimelineDot color="success" />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Typography fontWeight="bold">Retained 90 Days</Typography>
                    </TimelineContent>
                  </TimelineItem>
                )}
              </Timeline>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTimelineDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralTracking;
